import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useREInvestingSim, InvestmentProperty, Phase } from "@/hooks/useREInvestingSim";
import IntroScreen from "@/components/re-investing/IntroScreen";
import InvestorSetup from "@/components/re-investing/InvestorSetup";
import TutorialDeal from "@/components/re-investing/TutorialDeal";
import PropertyMarketplace from "@/components/re-investing/PropertyMarketplace";
import DealAnalyzer from "@/components/re-investing/DealAnalyzer";
import PortfolioDashboard from "@/components/re-investing/PortfolioDashboard";
import EventCard from "@/components/re-investing/EventCard";
import ResultPopup from "@/components/re-investing/ResultPopup";
import RoundSummary from "@/components/re-investing/RoundSummary";
import SummaryScreen from "@/components/re-investing/SummaryScreen";
import { Progress } from "@/components/ui/progress";
import SimulatorGate from "@/components/modules/SimulatorGate";
import LunimarScene from "@/components/lunimar/LunimarScene";
import { toast } from "sonner";
import DecisionConsequenceStrip from "@/components/re-investing/DecisionConsequenceStrip";
import { useREDecisionLog } from "@/hooks/useREDecisionLog";
import { useMemo } from "react";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

const REInvestingContent = () => {
  const sim = useREInvestingSim();
  const navigate = useNavigate();
  const [analyzingProperty, setAnalyzingProperty] = useState<InvestmentProperty | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [showRoundSummary, setShowRoundSummary] = useState(false);
  const [roundSummaryData, setRoundSummaryData] = useState<AnyValue>(null);
  const [localPhase, setLocalPhase] = useState<Phase>("intro");
  const decisions = useREDecisionLog(sim.sim?.id || null);

  useEffect(() => {
    sim.loadState();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, []);

  useEffect(() => {
    if (sim.sim) {
      const simPhase = sim.sim.current_phase as Phase;
      if (simPhase !== localPhase) {
        setLocalPhase(simPhase);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [sim.sim?.current_phase]);

  const phase = localPhase;

  const handleAdvanceRound = async () => {
    try {
      const res = await sim.advanceRound();
      if (res?.round_summary) {
        setRoundSummaryData(res.round_summary);
        setShowRoundSummary(true);
        decisions.recordRound(res.round_summary, res.sim || sim.sim);
      }
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || "Failed to advance round");
    }
  };

  const handleRoundSummaryContinue = () => {
    setShowRoundSummary(false);
    setRoundSummaryData(null);
  };

  const handleBuy = async (dpPct: number, rate: number, years: number) => {
    if (!analyzingProperty) return;
    try {
      const res = await sim.buyProperty(analyzingProperty.property_key, dpPct, rate, years);
      if (res?.purchase_summary) {
        setShowResult(true);
        decisions.recordPurchase(analyzingProperty, dpPct, years, res.sim || sim.sim);
      }
      setAnalyzingProperty(null);
    } catch (err: unknown) {
      toast.error(getErrorMessage(err) || "Purchase failed");
    }
  };

  const handlePropertyAction = async (property_key: string, actionType: string) => {
    const res = await sim.propertyAction(property_key, actionType);
    if (res && !(res).error) {
      decisions.recordPropertyAction(actionType, { ...(res).result, property_name: property_key }, (res).sim || sim.sim);
    }
    return res;
  };

  const handleSetup = async (capital: number, riskStyle: string, market: string) => {
    const res = await sim.startSim(capital, riskStyle, market);
    if ((res)?.sim?.id) {
      // sim id now available — record setup against the new log key.
      setTimeout(() => decisions.recordSetup(capital, riskStyle, market), 0);
    }
    return res;
  };

  const recap = useMemo(() => {
    if (!sim.sim || (phaseGuard(localPhase) === false)) return null;
    return decisions.buildRecap(sim.sim, {
      netWorth: sim.netWorth,
      totalPortfolioValue: sim.totalPortfolioValue,
      totalMortgageDebt: sim.totalMortgageDebt,
      monthlyCashFlow: sim.monthlyCashFlow,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim.sim, localPhase, decisions.log, sim.netWorth, sim.totalPortfolioValue, sim.totalMortgageDebt, sim.monthlyCashFlow]);

  if (sim.loading) return <div className="flex min-h-screen items-center justify-center bg-background"><p className="text-muted-foreground">Loading simulation...</p></div>;

  const renderPhase = () => {
    if (analyzingProperty) {
      return (
        <DealAnalyzer
          property={analyzingProperty}
          availableCash={sim.sim?.available_cash || 0}
          onBuy={handleBuy}
          onBack={() => setAnalyzingProperty(null)}
          processing={sim.processing}
        />
      );
    }

    switch (phase) {
      case "intro":
        return <IntroScreen onStart={() => setLocalPhase("setup")} />;
      case "setup":
        return <InvestorSetup onComplete={handleSetup} processing={sim.processing} />;
      case "tutorial":
        return <TutorialDeal onComplete={async () => {
          await sim.setPhase("marketplace");
        }} />;
      case "marketplace":
        return sim.sim ? (
          <PropertyMarketplace
            properties={sim.properties}
            availableCash={sim.sim.available_cash}
            onAnalyze={setAnalyzingProperty}
            onBack={() => setLocalPhase("ownership" as Phase)}
          />
        ) : null;
      case "ownership":
      case "expansion":
      case "advanced":
        return sim.sim ? (
          <PortfolioDashboard
            sim={sim.sim}
            totalPortfolioValue={sim.totalPortfolioValue}
            totalMortgageDebt={sim.totalMortgageDebt}
            totalEquity={sim.totalEquity}
            netWorth={sim.netWorth}
            monthlyCashFlow={sim.monthlyCashFlow}
            onAdvance={handleAdvanceRound}
            onPropertyAction={handlePropertyAction}
            onBuyMore={() => setLocalPhase("marketplace")}
            processing={sim.processing}
          />
        ) : null;
      case "summary":
      case "completed":
        return sim.sim ? (
          <SummaryScreen
            sim={sim.sim}
            netWorth={sim.netWorth}
            totalPortfolioValue={sim.totalPortfolioValue}
            totalMortgageDebt={sim.totalMortgageDebt}
            monthlyCashFlow={sim.monthlyCashFlow}
            onFinish={() => navigate("/")}
            onDashboard={() => navigate("/finances")}
            recap={recap}
            decisionLog={decisions.log}
          />
        ) : null;
      default:
        return <IntroScreen onStart={() => setLocalPhase("setup")} />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {phase !== "intro" && phase !== "summary" && phase !== "completed" && (
        <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-40">
          <div className="container max-w-5xl mx-auto py-3 px-4 space-y-2">
            <div className="flex items-center justify-between">
              <h1 className="font-heading font-bold text-sm text-foreground">Real Estate Investing</h1>
              {sim.sim && <span className="text-xs text-muted-foreground">Quarter {sim.sim.current_round}/{sim.sim.max_rounds}</span>}
            </div>
            {sim.sim && <Progress value={(sim.sim.current_round / sim.sim.max_rounds) * 100} className="h-1.5" />}
          </div>
        </header>
      )}
      <main className="container max-w-5xl mx-auto py-8 px-4">
        {showStrip(localPhase) && <DecisionConsequenceStrip entry={decisions.lastDecision} />}
        {renderPhase()}
      </main>

      {showRoundSummary && roundSummaryData && (
        <RoundSummary
          summary={roundSummaryData}
          onContinue={handleRoundSummaryContinue}
          hasEvent={!!sim.pendingEvent}
        />
      )}
      {!showRoundSummary && sim.pendingEvent && (
        <EventCard event={sim.pendingEvent} onChoose={async (i) => {
          const ev = sim.pendingEvent;
          const res = await sim.resolveEvent(ev.id, i);
          if (res && !(res).error) {
            decisions.recordEvent(ev, i, (res).outcome, (res).sim || sim.sim);
            if ((res).outcome) setShowResult(true);
          }
        }} processing={sim.processing} />
      )}
      {showResult && sim.lastResult && (
        <ResultPopup result={sim.lastResult} onClose={() => { setShowResult(false); sim.setLastResult(null); }} />
      )}
    </div>
  );
};

/* Show the consequence strip only on phases where a player is acting. */
function showStrip(phase: Phase) {
  return phase === "marketplace"
    || phase === "ownership"
    || phase === "expansion"
    || phase === "advanced";
}

function phaseGuard(phase: Phase) {
  return phase === "summary" || phase === "completed";
}

const REInvestingSimulator = () => (
  <SimulatorGate moduleKey="re_investing">
    <LunimarScene realmKey="re_investing">
      <REInvestingContent />
    </LunimarScene>
  </SimulatorGate>
);

export default REInvestingSimulator;
